
import React from "react";
import { BlogPost } from "@/types/blogTypes";
import PostHeader from "@/components/blog/PostHeader";
import PostContent from "@/components/blog/PostContent";
import PostCTA from "@/components/blog/PostCTA";
import RelatedPosts from "@/components/blog/RelatedPosts";
import { useBlogSeo } from "@/hooks/useBlogSeo";
import useSEO from "@/hooks/useSEO";
import { Link } from "react-router-dom";
import ViewTracker from "@/components/blog/ViewTracker";
import AuthorBio from "@/components/blog/AuthorBio";
import { repairDisplayText } from "@/utils/displayText";
import EmergencyGuideLinks from "@/components/letters/EmergencyGuideLinks";
import OccasionLetterCallout from "@/components/blog/OccasionLetterCallout";

interface BlogPostContentProps {
  post: BlogPost;
  relatedPosts: Array<{
    id: string | number;
    title: string;
    excerpt: string;
    slug: string;
    imageSrc: string;
  }>;
  slug?: string;
  skipSeo?: boolean; // Add this prop to optionally skip SEO
  skipViewTracking?: boolean; // Add this prop to optionally skip view tracking
}

/**
 * Main content component for a blog post
 */
const BlogPostContent = ({ post, relatedPosts, slug, skipSeo = false, skipViewTracking = false }: BlogPostContentProps) => {
  const seoProps = useBlogSeo(post, slug);
  const hasBodyH1 = /<h1\b/i.test(post.content);
  
  useSEO({
    title: seoProps.title,
    description: seoProps.description,
    canonicalUrl: seoProps.canonicalUrl,
    ogType: seoProps.ogType,
    noIndex: seoProps.noIndex,
    disabled: skipSeo,
    schema: seoProps.schema
  });

  return (
    <>
      {/* Only include ViewTracker if not skipped */}
      {!skipViewTracking && <ViewTracker post={post} />}
      <div className="w-full overflow-hidden">
        <article className="w-full bg-white">
          <div className="container mx-auto px-4 pt-6">
            <nav className="mb-6 text-sm" aria-label="Breadcrumb">
              <ol className="flex items-center space-x-2">
                <li><Link to="/" className="text-elfGreen-dark hover:underline">Home</Link></li>
                <li className="flex items-center">
                  <span className="mx-2">/</span>
                  <Link to="/blog/" className="text-elfGreen-dark hover:underline">Blog</Link>
                </li>
                <li className="flex items-center">
                  <span className="mx-2">/</span>
                  <span className="font-medium truncate max-w-[200px]">{repairDisplayText(post.title)}</span>
                </li>
              </ol>
            </nav>
          </div>

          <PostHeader 
            title={repairDisplayText(post.title)}
            date={new Date(post.created_at).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
            author={post.metadata?.author || "Elf Letter Team"}
            imageSrc={post.featured_image || "https://images.unsplash.com/photo-1607305387299-a3d9611cd469"}
            hasBodyH1={hasBodyH1}
          />

          <div className="w-full">
            <OccasionLetterCallout slug={post.slug} />
            <PostContent 
              content={post.content} 
              theme={post.metadata?.theme}
            />
            
            <div className="max-w-full mx-auto px-4 pb-24">
              <PostCTA source={`blog-${post.slug}`} />
              <AuthorBio author={post.metadata?.author || "Elf Letter Team"} />
              <EmergencyGuideLinks />
              
              {relatedPosts.length > 0 && (
                <RelatedPosts posts={relatedPosts} />
              )}
            </div>
          </div>
        </article>
      </div>
    </>
  );
};

export default BlogPostContent;
